/**
 * Pure helpers shared by the avatar primitives.
 *
 * Mirrors the mobile app's helpers in
 * `apps/mobile/features/data/adapters/organization.ts` and
 * `apps/mobile/features/tally/components/ClientAvatar.tsx`. Kept
 * purely functional so they can be unit tested without React.
 */

/**
 * Stable colour palette for fallback avatar tiles. Matches the
 * mobile palette one-for-one — picking the same colour for a given
 * id on web means an org's tile reads the same on both surfaces.
 */
export const AVATAR_COLORS = [
  '#A85528',
  '#3D4F86',
  '#3F7A4A',
  '#7A3F86',
  '#86713F',
  '#3F8686',
] as const;

/**
 * Deterministic colour for an entity by id. Same hash + modulo as
 * the mobile `pickColor` so the same org / client gets the same
 * tile colour regardless of platform.
 */
export function pickAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx]!;
}

/**
 * Up to two characters of client initials — first letter of up to
 * two whitespace-delimited words.
 *
 *   "Acme Studios"        → "AS"
 *   "Sarah Connor LLC"    → "SC"
 *   "joe"                 → "J"
 *   "  "                  → ""   (caller decides on a fallback glyph)
 */
export function clientInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Up to **three** characters of business initials — single-word
 * names take the first three letters, multi-word names take the
 * first letter of the first three words. Matches the mobile
 * `deriveInitials` so business tiles read identically on both
 * platforms.
 *
 *   "Joe"                 → "JOE"
 *   "Acme Studios LLC"    → "ASL"
 *   "Acme"                → "ACM"
 *   ""                    → "?"
 */
export function businessInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 3).toUpperCase();
  return parts
    .slice(0, 3)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * Heuristic for "is this a Commercial client?" — used to drive the
 * tinted background on `ClientAvatar` when no photo is set. Mirrors
 * the same heuristic in
 * `apps/mobile/features/data/adapters/client.ts` so commercial
 * clients read with the steel-blue treatment on both platforms.
 */
const COMMERCIAL_NAME_RE =
  /(\bLLC\b|\bInc\b|\bCo\b|\bCorp\b|\bMgmt\b|\bDental\b|\bCafe\b|\bWorking\b|\bApartments?\b)/i;

export type ClientTag = 'Residential' | 'Commercial';

export function inferClientTag(args: {
  name: string;
  company?: string | null;
}): ClientTag {
  if (args.company && args.company.trim().length > 0) return 'Commercial';
  if (COMMERCIAL_NAME_RE.test(args.name)) return 'Commercial';
  return 'Residential';
}
